import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LabelIcon from '@mui/icons-material/Label';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import InformationSubMenu from '@/components/nav/InformationSubMenu';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import PricingDetailsComp from '@/components/ui/PricingDetailsComp';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const FormW2C = () => {
  const [open, setOpen] = useState(false);

  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Extend your business tax return deadline with Form 7004.`,
  //   SubHeading:
  //     'E-file now and get more time to ensure accuracy. File now for peace of mind.',
  //   CtaText: 'E-file Form 7004 Now',
  //   CtaSubHeading: `Takes 3 steps and less than 5 minutes`,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=sticky`,
  //   isActive: true,
  // };

  return (
    <AppLayout
      //  LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-File W-2c for 2025: Correcting W-2 Errors Made Easy"
          description="Make W-2 corrections hassle-free for 2025. With TaxZerone, easily file W-2c forms online. Ensure accuracy and compliance in minutes!"
          keywords=""
          canonical="information-returns/efile-irs-form-w2-c/"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
          ogShareImg="assets/images/information-share-img.png"
        />
      }
    >
      <InformationSubMenu />
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                File{' '}
                <span className="highlight-container">
                  <span className="highlight">Form W-2c</span>
                </span>{' '}
                <Hidden smDown>
                  <br />
                </Hidden>{' '}
                for Tax Year 2025 with Ease
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Corrected wage and tax reporting with TaxZerone’s accurate and
                hassle-free e-filing solution for Form W-2c.
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=formw2c`}
                  >
                    E-file Form{' '}
                    <Box
                      component="span"
                      sx={{ ml: 1, color: '#eaff00', fontWeight: 700 }}
                    >
                      W-2c
                    </Box>
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/w2c-landing-img.webp" />
                <source srcSet="/assets/images/w2c-landing-img.png" />
                <img
                  src="/assets/images/w2c-landing-img.png"
                  alt="File Form W-2c for Tax Year 2025 with Ease"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/w2c-landing-img-sm.webp" />
                <source srcSet="/assets/images/w2c-landing-img-sm.png" />
                <img
                  src="/assets/images/w2c-landing-img-sm.png"
                  alt="File Form W-2c for Tax Year 2025 with Ease"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 30, sm: 26, xs: 22 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                Affordable Pricing
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                Start at just{' '}
                <Box
                  component="strong"
                  sx={{
                    color: '#ffe400',
                    fontSize: { xs: 24, sm: 30 },
                  }}
                >
                  $2.49
                </Box>{' '}
                with prices as low as{' '}
                <Box
                  component="strong"
                  sx={{
                    fontSize: { xs: 24, sm: 30 },
                  }}
                >
                  $0.59
                </Box>{' '}
                per form for bulk filings
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 160, md: 160 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              <Button
                size="large"
                variant="contained"
                href="/pricing/#informationreturns"
                sx={{
                  boxShadow: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  color: '#0a7581',
                  '&:hover, &:focus, &:active': {
                    color: '#fff',
                    backgroundColor: '#172b6b',
                  },
                }}
              >
                See Pricing
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 0, mb: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                For your return volume
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 4, md: 6 },
                  borderRadius: 10,
                  backgroundColor: '#f5f6f7',
                  maxWidth: 800,
                  mx: 'auto',
                  boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 4,
                    textAlign: 'center',
                  }}
                >
                  Filing Requirements for Form W-2c
                </Typography>

                <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                  Let&apos;s make your W-2c filing simple! Here&apos;s what
                  you&apos;ll need:
                </Typography>
                <Box
                  sx={{
                    justifyContent: 'center',
                    px: { xs: 0, sm: 2, md: 12 },
                  }}
                >
                  <List>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 'inherit',
                          mr: 1,
                          mt: 0.6,
                          color: '#1da94a',
                        }}
                      >
                        <LabelIcon />
                      </ListItemIcon>
                      <ListItemText sx={{}}>
                        <b> Employer Information:</b> Employer&apos;s Name, EIN
                        and Address
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 'inherit',
                          mr: 1,
                          mt: 0.6,
                          color: '#1da94a',
                        }}
                      >
                        <LabelIcon />
                      </ListItemIcon>
                      <ListItemText sx={{}}>
                        <b>Employee Information:</b> Employee’s Name, SSN and
                        Address
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 'inherit',
                          mr: 1,
                          mt: 0.6,
                          color: '#1da94a',
                        }}
                      >
                        <LabelIcon />
                      </ListItemIcon>
                      <ListItemText sx={{}}>
                        <b> Wage Information:</b> Corrected wages, Federal Tax
                        Withheld, Social Security wages, and Medicare wages
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 'inherit',
                          mr: 1,
                          mt: 0.6,
                          color: '#1da94a',
                        }}
                      >
                        <LabelIcon />
                      </ListItemIcon>
                      <ListItemText sx={{}}>
                        <b> State Tax Information:</b> Corrected Employer’s
                        State ID Number, State wages, and State income Tax
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                3 Simple Steps to Complete Your Form W-2c E-filing
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Filing your Form W-2c with TaxZerone is fast, easy, and secure.{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>
                Here’s how you can do it in just three simple steps:
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/w2c-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/w2c-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/w2c-steps-img.png"
                  alt="3 Simple Steps to Complete Your Form W-2c E-filing"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter Information for Form W-2c"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Information for Form W-2c
                    </Typography>
                    <Typography variant="body1">
                      Fill in your business details (EIN), employee&apos;s
                      information (corrected SSN, name), address, and the
                      corrected wage data.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Review & Transmit"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Review & Transmit
                    </Typography>
                    <Typography variant="body1">
                      Ensure accuracy with TaxZerone’s SSA validations, then
                      securely e-file your Form W-2c to the SSA.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Send Employee Copy"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Send Employee Copy
                    </Typography>
                    <Typography variant="body1">
                      Securely deliver the employee&apos;s copy via ZeroneVault
                      for safe electronic sharing, or opt for postal mailing to
                      provide a physical copy.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>

              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=formw2c`}
                >
                  E-file Form W-2c
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone for Form W-2c E-filing?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                TaxZerone is the trusted e-filing solution for businesses of all
                sizes. Here’s why:
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-rule-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-rule-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-rule-icon.png"
                      alt="Smart Validation System"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Smart Validation System
                </Typography>
                <Typography variant="body1">
                  Ensure your Form W-2c is fully compliant with SSA standards in
                  real-time, reducing the risk of costly mistakes and rejections
                  with our advanced validation system.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/upload-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/upload-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/upload-icon.png"
                      alt="Bulk Upload Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Bulk Upload Support
                </Typography>
                <Typography variant="body1">
                  Easily upload and file multiple W-2c corrections at once,
                  saving time and streamlining the e-filing process with
                  TaxZerone.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/email-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/email-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/email-icon.png"
                      alt="Share Employee Copies"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Share Employee Copies
                </Typography>
                <Typography variant="body1">
                  Seamlessly share Employee copies through ZeroneVault for
                  secure electronic delivery or opt for physical mailing.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/lowest-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/lowest-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/lowest-icon.png"
                      alt="Competitive Pricing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Competitive Pricing
                </Typography>
                <Typography variant="body1">
                  Enjoy industry-leading pricing designed to match your filing
                  volume, helping you keep costs under control without
                  compromising on service.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Form W-3c"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Form W-3c
                </Typography>
                <Typography variant="body1">
                  Get your Form W-3c, generated based on your W-2c correction,
                  at no extra charge—available directly from your TaxZerone
                  account.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/time-icon.png"
                      alt="Fast W-2c Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Fast W-2c Filing
                </Typography>
                <Typography variant="body1">
                  File your W-2c corrections in just a few minutes with our
                  simple, step-by-step process—making it quick and easy to stay
                  compliant.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Box sx={{ px: { md: 16, sm: 3, xs: 0 }, textAlign: 'center' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                    textAlign: 'center',
                  }}
                >
                  Important Deadlines for Filing Form W-2c
                </Typography>
                <Typography variant="body1">
                  The Form W-2c, used to correct errors on a previously filed
                  Form W-2, does not have a specific deadline. You are required
                  to file Form W-2c as soon as you discover the error to ensure
                  that the corrected information is provided promptly to both
                  the IRS and the affected employee(s). Timely corrections help
                  avoid penalties and potential confusion during tax filing.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: 5 }}>
          <PricingDetailsComp formName="Form W-2c" colorCode="#4269e2" />
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12} sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,

                  textAlign: 'center',
                }}
              >
                W-2c State Filing
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Did you know that some states require separate filings for Form
                W-2c in addition to the federal filing?
                <Hidden mdDown>
                  <br />
                </Hidden>
                Ensure compliance with state-specific tax laws by filing in all
                required jurisdictions.
              </Typography>
            </Grid>

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              justifyContent="center"
              sx={{
                '& .MuiPaper-root:hover': {
                  boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                  transition: '0.3s',
                  borderColor: '#ccc',
                },
              }}
            >
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/alabama-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',

                      cursor: 'pointer',
                      // width: { md: 'auto', sm: '250px !important', xs: 'auto' },
                      maxWidth: '100%',
                      minHeight: '100%',
                      alignSelf: 'center',
                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',
                        alignSelf: 'center',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',

                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/alabama-flag.svg"
                        loading="lazy"
                        alt="alabama"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',

                        // sm: '15px !important',
                      }}
                    >
                      Alabama
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/arizona-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      width: { md: 'auto', sm: '250px', xs: 'auto' },
                      maxWidth: '100%',
                      minHeight: '100%',
                      alignSelf: 'center',
                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',
                        alignSelf: 'center',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/arizona-flag.svg"
                        loading="lazy"
                        alt="arizona-flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Arizona
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/arkansas-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/arkansas-flag.svg"
                        loading="lazy"
                        alt="Arkansas flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Arkansas
                    </Typography>
                  </Paper>
                </Link>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/colorado-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/colorado-flag.svg"
                        loading="lazy"
                        alt="colorado flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Colorado
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/connecticut-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/connecticut-flag.svg"
                        loading="lazy"
                        alt="connecticut flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Connecticut
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/delaware-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/delaware-flag.svg"
                        loading="lazy"
                        alt="delaware flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Delaware
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/district-of-columbia-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/dist-columbia-flag.svg"
                        loading="lazy"
                        alt="Dist. of Columbia flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Dist. of Columbia
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/georgia-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/georgia-flag.svg"
                        loading="lazy"
                        alt="georgia flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Georgia
                    </Typography>
                  </Paper>
                </Link>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/hawaii-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/hawaii-flag.svg"
                        loading="lazy"
                        alt="hawaii flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Hawaii
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/idaho-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/flag-idaho.svg"
                        loading="lazy"
                        alt="Idaho flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Idaho
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/illinois-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/illinois-flag.svg"
                        loading="lazy"
                        alt="illinois flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Illinois
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/indiana-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/indiana-flag.svg"
                        loading="lazy"
                        alt="indiana flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Indiana
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/iowa-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/iowa-flag.svg"
                        loading="lazy"
                        alt="iowa flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Iowa
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/kansas-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/kansas-flag.svg"
                        loading="lazy"
                        alt="kansas flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Kansas
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/kentucky-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/kentucky-flag.svg"
                        loading="lazy"
                        alt="kentucky flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Kentucky
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/louisiana-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/louisiana-flag.svg"
                        loading="lazy"
                        alt="Louisiana flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Louisiana
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/maine-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/maine-flag.svg"
                        loading="lazy"
                        alt="Maine flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Maine
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/maryland-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/maryland-flag.svg"
                        loading="lazy"
                        alt="Maryland flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Maryland
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/massachusetts-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/massachusetts-flag.svg"
                        loading="lazy"
                        alt="Massachusetts flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Massachusetts
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/michigan-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/michigan-flag.svg"
                        loading="lazy"
                        alt="michigan flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Michigan
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/minnesota-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/minnesota-flag.svg"
                        loading="lazy"
                        alt="minnesota flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Minnesota
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/mississippi-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/mississippi-flag.svg"
                        loading="lazy"
                        alt="mississippi flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Mississippi
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/missouri-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/missouri-flag.svg"
                        loading="lazy"
                        alt="Missouri flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Missouri
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/montana-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/montana-flag.svg"
                        loading="lazy"
                        alt="Montana flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Montana
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/nebraska-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/nebraska-flag.svg"
                        loading="lazy"
                        alt="Nebraska flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Nebraska
                    </Typography>
                  </Paper>
                </Link>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/new-jersey-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/new-jersey-flag.svg"
                        loading="lazy"
                        alt="New Jersey flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      New Jersey
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/new-mexico-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/new-mexico-flag.svg"
                        loading="lazy"
                        alt="New mexico flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      New Mexico
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/north-carolina-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/north-carolina-flag.svg"
                        loading="lazy"
                        alt="North Carolina flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      North Carolina
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/north-dakota-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/north-dakota-flag.svg"
                        loading="lazy"
                        alt="North Dakota flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      North Dakota
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/ohio-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/ohio-flag.svg"
                        loading="lazy"
                        alt="ohia flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Ohio
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/oklahoma-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/oklahoma-flag.svg"
                        loading="lazy"
                        alt="Oklahoma flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Oklahoma
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/oregon-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/oregon-flag.svg"
                        loading="lazy"
                        alt="Oregon flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Oregon
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/pennsylvania-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/pennsylvania-flag.svg"
                        loading="lazy"
                        alt="Pennsylvania flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Pennsylvania
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/rhode-island-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/rhode-island-flag.svg"
                        loading="lazy"
                        alt="Rhode Island flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Rhode Island
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/south-carolina-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/south-carolina-flag.svg"
                        loading="lazy"
                        alt="South Carolina flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      South Carolina
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/utah-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/utah-flag.svg"
                        loading="lazy"
                        alt="utah"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Utah
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/vermont-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/vermont-flag.svg"
                        loading="lazy"
                        alt="Vermont flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Vermont
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/virginia-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/virginia-flag.svg"
                        loading="lazy"
                        alt="Virginia flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Virginia
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/west-virginia-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/west-virginia-flag.svg"
                        loading="lazy"
                        alt="West Virginia flag"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      West Virginia
                    </Typography>
                  </Paper>
                </Link>
              </Grid>

              <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
                <Link
                  href="/form-w2-state-filing/wisconsin-w-2-filing-requirements/"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 3 },
                      borderRadius: 3,
                      display: { md: 'grid', sm: 'flex', xs: 'grid' },
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 1,
                      placeContent: 'start',
                      backgroundColor: '#fdfdfd',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      alignItems: 'center',
                      boxAlign: 'center',
                      maxWidth: '100%',
                      minHeight: '100%',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px !important',
                        height: '17px !important',
                        position: 'relative',

                        '& .state-flag': {
                          position: 'absolute',
                          height: '100%',
                          width: '100%',
                          inset: '0px',
                          color: 'transparent',
                        },
                      }}
                    >
                      <img
                        srcSet="/assets/images/state-flag/wisconsin-flag.svg"
                        loading="lazy"
                        alt="Wisconsin"
                        width="100%"
                        height="100%"
                        className="state-flag"
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '15px !important',
                      }}
                    >
                      Wisconsin
                    </Typography>
                  </Paper>
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Schedule Filing: Plan Ahead and Stay On Track
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center' }}>
                Want to ensure your filings are submitted on time without the
                stress? With Schedule Filing, you can prepare your Form W-2c
                filings in advance, and we’ll take care of the submission when
                the time comes.
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            mt={2}
            py={{ xs: 0, sm: 4, md: 5 }}
            className="MuiGrid-root-width"
            justifyContent="center"
            sx={{ position: 'relative' }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/due-date-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/due-date-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/due-date-icon.png"
                      alt="Choose Your Filing Date"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Choose Your Filing Date
                </Typography>
                <Typography variant="body1">
                  Set your preferred filing date, and we’ll ensure your forms
                  are submitted to the IRS promptly and accurately.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/review-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/review-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/review-icon.png"
                      alt="Ensure Accuracy"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Ensure Accuracy
                </Typography>
                <Typography variant="body1">
                  Employees can review their Form W-2c forms before submission,
                  allowing them to identify and correct any errors in advance
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/smart-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/smart-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/smart-icon.png"
                      alt="Avoid the Need for IRS Correction Forms"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Avoid the Need for IRS Correction Forms
                </Typography>
                <Typography variant="body1">
                  Scheduling your filing provides an opportunity to validate all
                  details beforehand, reducing errors and eliminating the hassle
                  of filing IRS correction forms later.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid
                size={12}
                sx={{
                  position: 'absolute',
                  zIndex: 1,
                  top: '60px',
                  right: 35,
                  opacity: 0.5,
                }}
              >
                <img
                  src="/assets/images/dot-shape.svg"
                  alt=""
                  width="100%"
                  height="133"
                  style={{ width: '100%', maxWidth: 1100, height: 133 }}
                />
              </Grid>
            </Hidden>
            <Grid size={12}>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 0,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=formw2c`}
                >
                  Schedule Your W-2c Now!
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Share Employee Copies with Ease
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center' }}>
                TaxZerone makes it simple to deliver Form W-2c copies to your
                Employee securely and on time.
                <Hidden mdDown>
                  {' '}
                  <br />
                </Hidden>
                Choose the method that works best for your business:
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            mt={2}
            // py={{ xs: 0, sm: 4, md: 7 }}
            className="MuiGrid-root-width"
            justifyContent="center"
            sx={{ position: 'relative', mb: 4 }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'left',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                  boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 5px 0px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'transparent',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                    m: 0,
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/vault-logo.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/vault-logo.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/vault-logo.png"
                      alt="Secure Delivery via ZeroneVault"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '20px !important',
                      fontWeight: 600,
                      textAlign: 'left',
                      mt: 1,
                      mb: 2,
                    }}
                  >
                    Secure Delivery via ZeroneVault{' '}
                  </Typography>
                  <Typography variant="body1">
                    Share employee copies electronically through ZeroneVault, a
                    secure and user-friendly platform. Employees can access
                    their forms instantly, without the need for printing or
                    mailing.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  pt: 5,
                  pb: 5,
                  textAlign: 'left',
                  borderWidth: 3,
                  borderColor: '#626262',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#fff',
                  boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 5px 0px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/email-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/email-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/email-icon.png"
                      alt="Traditional Postal Mailing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Box sx={{}}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '20px !important',
                      fontWeight: 600,
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    Traditional Postal Mailing{' '}
                  </Typography>
                  <Typography variant="body1">
                    Prefer a physical copy? TaxZerone also offers postal mailing
                    services. Employee copies are delivered promptly, ensuring
                    compliance with IRS deadlines.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Get Started with TaxZerone Today
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Filing Form W-2c online has never been easier. With TaxZerone, you
          can:
        </Typography>
        <Box
          sx={{
            textAlign: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Stack
            spacing={1}
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              m: 0,
            }}
          >
            <Box>
              <List>
                <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 'inherit',
                      mr: 1,
                      mt: 0.6,
                      color: '#1d1917',
                    }}
                  >
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText sx={{ color: '#f3f3f3' }}>
                    File quickly and accurately.
                  </ListItemText>
                </ListItem>
                <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 'inherit',
                      mr: 1,
                      mt: 0.6,
                      color: '#1d1917',
                    }}
                  >
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText sx={{ color: '#f3f3f3' }}>
                    Stay compliant with IRS requirements.
                  </ListItemText>
                </ListItem>
              </List>
            </Box>
            <Box>
              <List>
                <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 'inherit',
                      mr: 1,
                      mt: 0.6,
                      color: '#1d1917',
                    }}
                  >
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText sx={{ color: '#f3f3f3' }}>
                    Save time with bulk upload.
                  </ListItemText>
                </ListItem>
                <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 'inherit',
                      mr: 1,
                      mt: 0.6,
                      color: '#1d1917',
                    }}
                  >
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText sx={{ color: '#f3f3f3' }}>
                    File at the best price in the industry.
                  </ListItemText>
                </ListItem>
              </List>
            </Box>
          </Stack>
        </Box>
        <Typography variant="body1" sx={{ color: '#f3f3f3' }}>
          Start your e-filing process today and complete your Form W-2c in just
          3 simple steps!
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=formw2c`}
          >
            File Form W-2c Now!
          </Button>
        </Box>
      </Box>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 3 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is Form W-2c?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      Form W-2c, the{' '}
                      <span style={{ fontWeight: 600 }}>
                        &quot;Corrected Wage and Tax Statement,&quot;
                      </span>{' '}
                      is used to fix errors on previously submitted Form W-2.
                      Employers use this form to correct inaccuracies in the
                      wage and tax information they reported to employees and
                      the IRS. If you discover any mistakes on your previously{' '}
                      <Link
                        href="/information-returns/efile-irs-form-w2/"
                        sx={{ color: '#0202ff' }}
                      >
                        filed Form W-2
                      </Link>
                      , you must{' '}
                      <span style={{ fontWeight: 600 }}> file Form W-2c</span>{' '}
                      to make the necessary corrections.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Who Needs to File Form W-2c?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      Employers who have filed a Form W-2 that contains errors
                      related to employee wages, tax withholding, or other
                      reported information must file Form W-2c. This form is
                      used to correct any inaccuracies, including incorrect
                      amounts for Social Security wages, Medicare wages, or
                      federal and state tax withholdings.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What is the difference between Form W-2c and Form W-2?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form W-2
                      </Link>{' '}
                      is used to report an employee’s annual wages and the taxes
                      withheld from their paycheck to the SSA and the IRS. Form
                      W-2c, on the other hand, is specifically used for
                      correcting errors made on a previously filed W-2 form. The
                      W-2c essentially serves as an amended version of the
                      original W-2 to reflect any discrepancies.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. When is the deadline to file Form W-2c?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      There is no specific deadline for filing Form W-2c
                      (Corrected Wage and Tax Statement). Employers must file
                      this form as soon as they discover an error on a
                      previously submitted Form W-2 to ensure accurate tax
                      reporting for their employees.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Key Points to Remember:
                    </Typography>
                    <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                      <li>
                        <b> Timeliness is essential:</b> Correcting the error
                        promptly helps avoid penalties and ensures employees can
                        use accurate information for their tax filings.
                      </li>
                      <li>
                        <b> Notify employees:</b> Provide employees with the
                        corrected W-2c as soon as possible so they can adjust
                        their tax filings if necessary.
                      </li>
                      <li>
                        <b> File with the SSA:</b> Submit the corrected form to
                        the Social Security Administration (SSA) immediately
                        after completing it.
                      </li>{' '}
                    </Box>
                    <Typography variant="body1">
                      {' '}
                      If the error impacts other tax forms, such as the
                      employer&apos;s quarterly tax returns, additional
                      corrections might also be required.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. What Are the Penalties for Late Filing of Form W-2c?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Filing Form W-2c, the Corrected Wage and Tax Statement,
                      after the deadline can result in penalties from the
                      Internal Revenue Service (IRS). These penalties vary based
                      on how late the form is filed and the size of the
                      business. For the 2025 tax year, the penalties are as
                      follows:
                    </Typography>
                    <Box component="div" sx={{ mt: 2 }}>
                      <TableContainer
                        sx={{ border: '1px solid #828282', borderRadius: 2 }}
                      >
                        <Table sx={{}} aria-label="Due Date Chart">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#ccc' }}>
                              <TableCell
                                sx={{
                                  color: '#444',
                                  fontWeight: 600,
                                  width: 300,
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                }}
                              >
                                Delay Period
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: '#444',
                                  fontWeight: 600,
                                  width: 300,
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                }}
                              >
                                Penalty per Form
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: '#444',
                                  fontWeight: 600,
                                  width: 300,
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                }}
                              >
                                Maximum Penalty for Small Businesses (Gross
                                receipts ≤ $5 million)
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: '#444',
                                  fontWeight: 600,
                                  width: 300,
                                  textAlign: 'center',
                                  borderBottom: '1px solid #828282',
                                }}
                              >
                                Maximum Penalty for Larger Businesses
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  backgroundColor: '#fff',
                                  textAlign: 'center',
                                }}
                              >
                                Within 30 days
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $60
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: '1px solid #828282',
                                  borderRight: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $683,000
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $239,000
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  backgroundColor: '#fff',
                                  textAlign: 'center',
                                }}
                              >
                                31 days late through August 1
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $130
                              </TableCell>

                              <TableCell
                                sx={{
                                  borderBottom: '1px solid #828282',
                                  borderRight: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $683,000
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $2,049,000
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  backgroundColor: '#fff',
                                  textAlign: 'center',
                                }}
                              >
                                After August 1 or not filed
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $340
                              </TableCell>

                              <TableCell
                                sx={{
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                  borderBottom: '1px solid #828282',
                                  borderRight: '1px solid #828282',
                                }}
                              >
                                $4,098,500
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: '1px solid #828282',
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $1,366,000
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',
                                  backgroundColor: '#fff',
                                  textAlign: 'center',
                                }}
                              >
                                Intentional disregard
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',

                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                $680
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: '1px solid #828282',

                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                No limit
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: 'center',
                                  backgroundColor: '#fff',
                                }}
                              >
                                No limit
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{ mt: 2, mb: 0, alignItems: 'end' }}
                    >
                      <b>Note:</b> A &quot;small business&quot; is defined by
                      the IRS as one with average annual gross receipts of $5
                      million or less over the past three tax years.
                    </Alert>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default FormW2C;
